import React, { useState } from 'react';
import { Badge, Dropdown, List, Empty, Button, Spin } from 'antd';
import {
  BellOutlined,
  MessageOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export const NotificationDropdown: React.FC = () => {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'message' && notification.data?.conversationId) {
      navigate('/dashboard/messages');
      setVisible(false);
    } else if (
      notification.type === 'listing' &&
      notification.data?.listingId
    ) {
      navigate(`/listing/${notification.data.listingId}`);
      setVisible(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <MessageOutlined style={{ color: '#1890ff' }} />;
      default:
        return <BellOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = dayjs(dateString);
    const now = dayjs();
    const diffDays = now.diff(date, 'day');

    if (diffDays === 0) {
      return date.format('h:mm A');
    } else if (diffDays === 1) {
      return t('notifications.yesterday');
    } else if (diffDays < 7) {
      return date.format('ddd');
    } else {
      return date.format('MMM D');
    }
  };

  const notificationContent = (
    <div style={{ width: 350, maxHeight: 400, overflowY: 'auto' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <strong>{t('notifications.title')}</strong>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={async e => {
              e.stopPropagation();
              await markAllAsRead();
            }}
          >
            Mark all as read
          </Button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('notifications.empty')}
          style={{ padding: '24px' }}
        />
      ) : (
        <List
          dataSource={notifications}
          renderItem={notification => (
            <List.Item
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: notification.read ? 'transparent' : '#f0f7ff',
                borderLeft: notification.read ? 'none' : '3px solid #1890ff',
              }}
              onClick={() => handleNotificationClick(notification)}
              onMouseEnter={e => {
                if (notification.read) {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }
              }}
              onMouseLeave={e => {
                if (notification.read) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                } else {
                  e.currentTarget.style.backgroundColor = '#f0f7ff';
                }
              }}
            >
              <List.Item.Meta
                avatar={getNotificationIcon(notification.type)}
                title={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span
                      style={{
                        fontWeight: notification.read ? 'normal' : 'bold',
                      }}
                    >
                      {notification.title}
                    </span>
                    {!notification.read && (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#1890ff',
                          display: 'inline-block',
                        }}
                      />
                    )}
                  </div>
                }
                description={
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      {notification.message}
                    </div>
                    <small style={{ color: '#999' }}>
                      {formatTime(notification.createdAt)}
                    </small>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => notificationContent}
      trigger={['click']}
      open={visible}
      onOpenChange={setVisible}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" offset={[-5, 5]}>
        <BellOutlined
          style={{
            fontSize: '20px',
            color: 'var(--color1)',
            cursor: 'pointer',
            padding: '8px',
          }}
        />
      </Badge>
    </Dropdown>
  );
};
